import React, {useState, useEffect, useCallback} from 'react';
import {useAppDispatch, useAppSelector} from "lib/hooks";
import {FieldValueChangeArgs, useForm} from "lib/formHook";
import {DesignacaoFazendaList, FazendaController, FazendaObject} from "models/Fazenda";
import {getFormDefFazenda} from "../controller";
import {FormDefinition, FormState} from "lib/forms";
import Form from "components/Form";
import {ModelFormField} from "components/ModelFormHOC";
import {useGetVeterinarios} from "lib/DataHooks/veterinarios";
import {useEstados, useGetCidades} from "lib/DataHooks/base";
import {actions} from "lib/app";
import SaveButton from "components/SaveButton";
import {PendingProprietario} from "../../Proprietarios/FormProprietario/controller";
import WidgetProprietario from "./WidgetProprietario";
import {ModalContent, ModalFooter} from "../../../components/Modal";
import {ProprietarioController} from "../../../models/Proprietario";
import firebase from "firebase/compat/app";

interface CadastroFazendaProps {
  instance?: FazendaObject;
  pendingValues?: FazendaObject;
  proprietario: PendingProprietario;
  onSubmit: () => void;
  onClickProprietario: (values: any) => void;
}

const _getInitialValues = (instance?: FazendaObject, pendingValues?: FazendaObject): FazendaObject | undefined => {
  if (!instance && !pendingValues)
    return undefined;

  const res = {};
  if (instance)
    Object.assign(res, instance);

  if (pendingValues)
    Object.assign(res, pendingValues);

  return res as FazendaObject;
}

const CadastroFazenda: React.FC<CadastroFazendaProps> = (props) => {
  const {instance, proprietario, pendingValues} = props;
  const user = useAppSelector(state => state.app.user);
  const dispatch = useAppDispatch();
  const [formDef, setFormDef] = useState<FormDefinition>(getFormDefFazenda());

  const getInitialValues = useCallback(() => {
    return _getInitialValues(instance, pendingValues);
  }, [instance, pendingValues]);

  const initialValues = getInitialValues();
  const form = useForm(formDef, initialValues);
  const {setFieldValue} = form;
  const [saving, setSaving] = useState(false);

  // form states
  const [tipo, setTipo] = useState<string[]>([]);
  const [veterinarios, loadingVeterinarios] = useGetVeterinarios({
    userId: user.uid,
  });
  const [estados, loadingEstados] = useEstados();
  const [estado, setEstado] = useState<string>();
  const [cidades, loadingCidades] = useGetCidades({estado}, {
    enabled: !!estado,
  });

  // load instance.estado
  useEffect(() => {
    if (instance) {
      const _estado= instance.estado;
      if (_estado && !loadingEstados && !estado) {
        setEstado(_estado);
      }
    }
  }, [instance, estado, estados, loadingEstados]);

  // load instance.tipo
  useEffect(() => {
    if (instance) {
      const _tipo = instance.tipo;
      if (_tipo) {
        // console.log('LOAD TIPO DEFAULT', _tipo);
        setTipo(_tipo);
      }
    }
  }, [instance]);

  // update formDef e formState on change tipo
  useEffect(() => {
    if (tipo) {
      const corte = tipo.indexOf('Corte') !== -1;
      const leite = tipo.indexOf('Leite') !== -1;

      // atual
      const _corte = formDef.sistema_producao.required;
      const _leite = formDef.sistema_producao_leite.required;

      if (corte !== _corte || leite !== _leite) {
        const newDef = {
          ...formDef,
          sistema_producao: {...formDef.sistema_producao, required: corte},
          sistema_producao_leite: {...formDef.sistema_producao_leite, required: leite},
        }
        // console.log('CHANGED TIPO', tipo);
        setFormDef(newDef);
      }
    }
  }, [tipo, formDef]);

  const onClickProprietario = () => {
    const values = getCleanedFormValues(form.values);
    props.onClickProprietario(values);
  }

  const onChangeEstado = (value: string) => {
    setEstado(value);
    setFieldValue([{fieldName: 'cidade', value: ''}]);
  }

  const onChangeTipo = (value: any, _formState: FormState) => {
    // preparar
    // console.log('STATE TIPO', _formState.tipo, value);
    let _tipo: string[];
    if (value.checked) {
      _tipo = [..._formState.tipo.value, value.value];
    } else {
      _tipo = tipo.filter((i: string) => i !== value.value);
    }
    // console.log('TIPO', _tipo);
    setTipo(_tipo);

    // ajustar sistema de producao
    const changes: FieldValueChangeArgs[] = [];
    if (!value.checked) {
      if (value.label === 'Leite') {
        changes.push({fieldName: 'sistema_producao_leite', value: ''});
      } else if (value.label === 'Corte') {
        changes.push({fieldName: 'sistema_producao', value: []});
        changes.push({fieldName: 'possui_estacao_monta', value: null});
      }
    }

    if (changes.length > 0)
      setFieldValue(changes);
  };

  const getCleanedFormValues = (values: any): any => {
    if (!veterinarios)
      throw new Error('Lista de veterinários não carregada, tente novamente.');

    const {veterinario} = values;
    const res = {...values};

    if (veterinario) {
      // suporte para alteracao do tipo de campo veterinario
      res.veterinario = veterinario.map((id: string) => (
        {
          id,
          label: veterinarios.find(i => i.id === id)?.nome,
        }
      ));
    }
    return res;
  }

  const onChangeResponsavel = (value: string) => {
    // ajustar campos requeridos
    let gereReq = false;
    let vetReq = false;

    if (value === 'Gerente')
      gereReq = true;
    else if (value === 'Veterinário')
      vetReq = true;

    setFormDef(state => ({
      ...state,
      gerente: {...state.gerente, required: gereReq},
      veterinario: {...state.veterinario, required: vetReq},
    }));
  }

  const onSubmit = async () => {
    if (!veterinarios) {
      dispatch(actions.notifyError('Lista de veterinários não carregada, tente novamente.'));
      return;
    }

    // validar
    const {success, values: _values, messagesFlat} = form.validate();
    if (!success) {
      dispatch(actions.notifyError(messagesFlat.join('<br/>')));
      return;
    }

    const model = new FazendaController(user);
    if (instance)
      model.setInstance(instance);

    // ajustar values
    const values = getCleanedFormValues(_values);

    model.setValues(values);
    const {success: cleanSuccess, errors} = model.clean();
    if (!cleanSuccess) {
      dispatch(actions.notifyError(errors.getFlat().join('<br/>')));
      return;
    }

    // setar _proprietario
    const _proprietario = new ProprietarioController(user);
    if (proprietario.mode === 'use')
      _proprietario.setInstance(proprietario.instance);
    else
      _proprietario.setValues(proprietario.instance);

    setSaving(true);

    let refProp: firebase.firestore.DocumentReference;
    if (proprietario.mode === 'new') {
      refProp = await _proprietario.save();
    } else {
      refProp = _proprietario.getRef();
    }

    model.setValue('_proprietario', refProp);

    await model.save();
    setSaving(false);
    if (model.isNew)
      dispatch(actions.notifySuccess('Fazenda inserida com sucesso.'));
    else
      dispatch(actions.notifySuccess('Fazenda atualizada com sucesso.'));
    props.onSubmit();
  }

  return (
    <>
      <ModalContent>
        <Form form={form}>
          <div className="text-xl font-hairline">
            Principal
          </div>
          <WidgetProprietario
            pendingInstance={proprietario}
            label="Proprietário"
            onClick={onClickProprietario}
          />

          <ModelFormField
            field="designacao"
            options={DesignacaoFazendaList}
          />
          <ModelFormField field="nome"/>

          <ModelFormField field="matriz"/>
          <ModelFormField
            field="tipo"
            options={['Corte', 'Leite']}
            onChange={onChangeTipo}
          />

          <ModelFormField
            field="responsavel"
            options={['Veterinário', 'Gerente', 'Proprietário']}
            onChange={onChangeResponsavel}
          />

          <ModelFormField
            field="veterinario"
            options={veterinarios}
            optionLabel="nome"
            optionValue="id"
            loading={loadingVeterinarios}
            interfaceOptions={{cssClass: 'app-select-alert wide'}}
          />

          <ModelFormField field="gerente"/>
          <ModelFormField field="telefone"/>
          <ModelFormField field="email"/>
          <ModelFormField
            field="estado"
            options={estados}
            optionLabel="uf"
            optionValue="uf"
            loading={loadingEstados}
            onChange={onChangeEstado}
          />
          <ModelFormField
            field="cidade"
            options={cidades}
            optionLabel="nome"
            optionValue="nome"
            loading={loadingCidades}
            disabled={!estado}
          />

          <div className="text-xl font-hairline mt-4">
            Localização
          </div>
          <ModelFormField field="latitude"/>
          <ModelFormField field="longitude"/>

          <div className="text-xl font-hairline mt-4">
            Informações adicionais
          </div>
          <ModelFormField field="tem_calendario_sanitario"/>

          {
            tipo.indexOf('Corte') !== -1 && (
              <ModelFormField
                field="sistema_producao"
                options={['Cria', 'Recria', 'Engorda', 'Confinamento']}
              />
            )
          }
          {
            tipo.indexOf('Leite') !== -1 && (
              <ModelFormField
                field="sistema_producao_leite"
                options={['A pasto', 'Free Stall', 'Compost Barn']}
              />
            )
          }
          {
            tipo.indexOf('Corte') !== -1 && (
              <ModelFormField field="possui_estacao_monta"/>
            )
          }
        </Form>
      </ModalContent>

      <ModalFooter
        message="Os campos marcados com * são obrigatórios."
      >
        <div>
          <SaveButton
            onClick={onSubmit}
            loading={saving}
          />
        </div>
      </ModalFooter>
    </>
  );
}

export default CadastroFazenda;
